import { useCallback, useEffect, useState } from 'react';
import { useOutletContext, Link, Outlet } from 'react-router-dom';
import type {
  HeizlastUebersicht, Heizlastbefund, Zuordnungsergebnis,
  GegVerknuepfungsergebnis, Pruefergebnis,
  HeizlastStandortstand, HeizlastExternwerte, HeizlastBereich,
  HeizlastReferenzwerte, HeizlastLaufKopf, HeizlastLaufErgebnis,
  HeizlastLaufDetail, AuslegungslaufWahl,
} from '../api';
import { Raumergebnisse } from './Raumergebnisse';
import { Berichtsknoepfe } from './Lastbericht';
import { Heizjahresgang } from './Heizjahresgang';
import { Fachaktionen } from './Fachaktionen';
import type { Rahmen } from './Projektrahmen';
import { LocalTabs } from '../rahmen/LocalTabs';
import { Raumstandhinweis } from './Raumstandhinweis';
import { Auslegungshinweis, Kennzeichnen, Laufmarke } from './heizlastkuehllast/Laufkennzeichnung';
import { Annahmenhinweis } from './heizlastkuehllast/AnnahmenVorgaben';
import { Feld } from '../muster';
import { IconWarnung, IconOk } from '../Icons';

/**
 * Die Gebäudehülle — Wände, Fenster, Türen, Dächer, Böden, Gelände.
 *
 * Auftragsart 70 liest sie, und drei Schritte machen daraus Rechenkost:
 * Zuordnung, Nachweisverknüpfung, Prüfung. Alle drei sind ausdrückliche
 * Knöpfe und keine Nebeneffekte der Aufnahme — sie ersetzen einen Bestand,
 * auf dem eine Berechnung aufgebaut sein kann.
 *
 * Kein Knopf zum Zurückschreiben. Die Hülle gehört dem Architekten.
 */
export function Gebaeudehuelle() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [uebersicht, setUebersicht] = useState<HeizlastUebersicht | null>(null);
  const [befunde, setBefunde] = useState<Heizlastbefund[]>([]);
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [nurOffen, setNurOffen] = useState(true);

  const quelle = projekt.modelle.find(
    (m) => m.isActive && (m.rollen ?? []).includes('huelle'));

  const laden = useCallback(async () => {
    try {
      const [u, b] = await Promise.all([
        api.heizlastUebersicht(projekt.id),
        api.heizlastBefunde(projekt.id, nurOffen ? 'offen' : undefined),
      ]);
      setUebersicht(u);
      setBefunde(b.befunde);
      setFehler(null);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }, [api, projekt.id, nurOffen]);

  useEffect(() => { void laden(); }, [laden]);

  const schritt = async (
    name: string,
    aufruf: () => Promise<Zuordnungsergebnis | GegVerknuepfungsergebnis | Pruefergebnis>,
  ) => {
    setLaeuft(name);
    setMeldung(null);
    setFehler(null);

    try {
      const e = await aufruf();

      // Die Hinweise der Dienste sind die Aussage, nicht die Zahlen: Ein
      // ACHTUNG-Hinweis sagt, was fehlt, und das gehört vor die Statistik.
      //
      // Geprüft wird auf `zusammenfassung` und NICHT auf `hinweise`:
      // Pruefergebnis führt beides — `hinweise` ist dort die ANZAHL der
      // Hinweise, eine Zahl. Eine Prüfung auf `'hinweise' in e` trifft also
      // alle drei Formen, und der andere Zweig wäre unerreichbar.
      setMeldung('zusammenfassung' in e ? e.zusammenfassung : e.hinweise);

      await laden();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(null);
    }
  };

  const akzeptieren = async (b: Heizlastbefund) => {
    const grund = window.prompt(
      `Befund "${b.titel}" akzeptieren.\n\n`
      + 'Die Begründung erscheint im Berechnungsbericht und ist Pflicht:');

    if (!grund?.trim()) return;

    try {
      await api.heizlastBefundAkzeptieren(b.id, grund.trim());
      await laden();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  };

  const lese = uebersicht?.lesestand;

  return (
    <>
      <Fachaktionen
        api={api}
        rolleKey="huelle"
        leseArt={70}
        leseText="Gebäudehülle lesen"
      />

      {/* --- Der Weg zu den Kennwerten -------------------------------------- */}
      {/* Kein eigener Reiter, sondern von hier aus - wie die Rohrnetzberechnung
          von der Heizungsseite: Wer die U- und g-Werte pflegt, kommt vom
          Datenstand her und will wissen, was noch fehlt.

          Das Ziel steht ABSOLUT. Ein relatives to="huelle/bauteilarten" wird
          gegen die aktuelle Route aufgelöst, und die ist hier schon /huelle -
          es entstünde /projekte/:id/huelle/huelle/bauteilarten. Diesen Pfad
          gibt es nicht, path="*" schickt auf /, und im Frontend sieht das aus,
          als öffne der Knopf die Startseite. */}
      {projekt && (
      <div className="karte" style={{ marginTop: 16, display: 'flex',
                                      alignItems: 'center', gap: 12,
                                      flexWrap: 'wrap' }}>
        <Link to={`/projekte/${projekt.id}/huelle/bauteilarten`}
              className="knopf primaer">
          Bauteilarten und Kennwerte
        </Link>
        <span className="schwach" style={{ fontSize: 12 }}>
          Gruppe → Bauteilart → Typ. Dort stehen U-Wert, g-Wert und die übrigen
          thermischen Kennwerte je Bauart — mit ihrer Herkunft, und dort werden
          sie auch festgelegt.
        </span>
      </div>
      )}

      {/* --- Der Datenstand ------------------------------------------------ */}
      <div className="karte" style={{ marginTop: 16 }}>
        <div className="bereich-kopf">
          <h2 className="karte-titel" style={{ margin: 0 }}>Datenstand</h2>
          {uebersicht && (
            <span className={uebersicht.istFreigabefaehig ? 'marke ok' : 'marke warnung'}>
              {uebersicht.istFreigabefaehig
                ? 'freigabefähig'
                : `${uebersicht.offeneFehler} offene Fehler`}
            </span>
          )}
        </div>

        {lese ? (
          <p className="schwach" style={{ fontSize: 13 }}>
            Letzter Lauf auf <strong>{lese.modellname}</strong> vom{' '}
            {new Date(lese.createdAt).toLocaleString('de-DE')} —{' '}
            {lese.anzahlBauteile.toLocaleString('de-DE')} Bauteile,{' '}
            {lese.anzahlRaeume.toLocaleString('de-DE')} Räume,{' '}
            {lese.anzahlSegmente.toLocaleString('de-DE')} Begrenzungssegmente,{' '}
            {lese.anzahlSchichten.toLocaleString('de-DE')} Schichten.
          </p>
        ) : (
          <p className="schwach">Noch kein Leselauf übernommen.</p>
        )}

        {uebersicht?.nachweis && (
          <p className="schwach" style={{ fontSize: 13 }}>
            Wärmeschutznachweis <strong>{uebersicht.nachweis.kennung}</strong>{' '}
            Index {uebersicht.nachweis.nachweisindex}
            {uebersicht.nachweis.stand
              && ` vom ${new Date(uebersicht.nachweis.stand).toLocaleDateString('de-DE')}`}
          </p>
        )}

        {uebersicht && uebersicht.bauteile.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table className="liste">
              <thead>
                <tr>
                  <th>Bauteilart</th>
                  <th style={{ textAlign: 'right' }}>Anzahl</th>
                  <th style={{ textAlign: 'right' }}>Fläche m²</th>
                  <th style={{ textAlign: 'right' }}>mit Nachweistyp</th>
                </tr>
              </thead>
              <tbody>
                {uebersicht.bauteile.map((b) => (
                  <tr key={b.bauteilart}>
                    <td>{b.bauteilart}</td>
                    <td style={{ textAlign: 'right' }}>
                      {b.anzahl.toLocaleString('de-DE')}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {Math.round(b.flaecheM2).toLocaleString('de-DE')}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {b.mitNachweistyp === 0 ? (
                        <span className="schwach">—</span>
                      ) : (
                        `${b.mitNachweistyp.toLocaleString('de-DE')} / ${b.anzahl.toLocaleString('de-DE')}`
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* --- Die drei Schritte --------------------------------------------- */}
      <div className="karte" style={{ marginTop: 16 }}>
        <h2 className="karte-titel" style={{ marginTop: 0 }}>
          Aus der Aufnahme Rechenkost machen
        </h2>

        <p className="schwach" style={{ fontSize: 13 }}>
          Drei getrennte Schritte, weil sie verschieden oft laufen. Die Zuordnung
          nach einem neuen Architekturstand — sie ist teuer. Die
          Nachweisverknüpfung nach einer neuen Aliaszeile. Die Prüfung nach jeder
          Pflege.
        </p>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            className="betont"
            disabled={laeuft !== null}
            title="Raum-Bauteilgrenzen aus Raumbegrenzung, Host, Flächenüberschneidung und XY-Rückfall bilden"
            onClick={() => schritt('zuordnung', () => api.heizlastZuordnen(projekt.id))}
          >
            {laeuft === 'zuordnung' ? 'Zuordnung läuft …' : '1. Zuordnung bilden'}
          </button>

          <button
            disabled={laeuft !== null}
            title="thermal_construction_code gegen typ_code des GEG-Nachweises abgleichen"
            onClick={() => schritt('geg', () => api.heizlastGegVerknuepfen(projekt.id))}
          >
            {laeuft === 'geg' ? 'Verknüpfung läuft …' : '2. Nachweis verknüpfen'}
          </button>

          <button
            disabled={laeuft !== null}
            title="Datenstand prüfen und Befunde schreiben"
            onClick={() => schritt('pruefung', () => api.heizlastPruefen(projekt.id))}
          >
            {laeuft === 'pruefung' ? 'Prüfung läuft …' : '3. Prüfen'}
          </button>
        </div>

        {meldung && (
          <div className="hinweis" style={{ marginTop: 12, display: 'block' }}>
            {meldung.map((z, i) => (
              <div
                key={i}
                style={{
                  marginBottom: 4,
                  fontWeight: z.startsWith('ACHTUNG') ? 600 : 400,
                }}
              >
                {z}
              </div>
            ))}
          </div>
        )}

        {fehler && (
          <div className="hinweis warnung" style={{ marginTop: 12 }}>
            <span className="hinweis-zeichen">!</span>
            <div>{fehler}</div>
          </div>
        )}
      </div>

      {/* --- Die Randbedingungen ------------------------------------------- */}
      {uebersicht && uebersicht.zuordnungen.length > 0 && (
        <div className="karte" style={{ marginTop: 16 }}>
          <h2 className="karte-titel" style={{ marginTop: 0 }}>
            Zuordnungen nach Randbedingung
          </h2>

          <p className="schwach" style={{ fontSize: 13 }}>
            Die Randbedingung entscheidet über ΔT und den Reduktionsfaktor.
            <strong> unbekannt</strong> heißt: nicht ableitbar — und wird
            ausdrücklich nicht auf Außenluft geraten. Das wäre die scheinbar
            sichere Richtung, und eine zu große Heizlast fällt nie auf.
          </p>

          <div style={{ overflowX: 'auto' }}>
            <table className="liste">
              <thead>
                <tr>
                  <th>Randbedingung</th>
                  <th style={{ textAlign: 'right' }}>Flächen</th>
                  <th style={{ textAlign: 'right' }}>m²</th>
                  <th style={{ textAlign: 'right' }}>mit U-Wert</th>
                </tr>
              </thead>
              <tbody>
                {uebersicht.zuordnungen.map((z) => (
                  <tr key={z.randbedingung}>
                    <td>
                      {z.randbedingung === 'unbekannt' ? (
                        <strong>{z.randbedingung}</strong>
                      ) : z.randbedingung}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {z.anzahl.toLocaleString('de-DE')}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {Math.round(z.flaecheM2).toLocaleString('de-DE')}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {z.mitUWert.toLocaleString('de-DE')} / {z.anzahl.toLocaleString('de-DE')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* --- Die Prüfungen -------------------------------------------------- */}
      <div className="karte" style={{ marginTop: 16 }}>
        <div className="bereich-kopf">
          <h2 className="karte-titel" style={{ margin: 0 }}>Prüfungen</h2>
          <label style={{ fontSize: 13, display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={nurOffen}
              onChange={(e) => setNurOffen(e.target.checked)}
            />
            nur offene
          </label>
        </div>

        {befunde.length === 0 ? (
          <p className="schwach" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <IconOk size={15} />
            {nurOffen ? 'Kein offener Befund.' : 'Kein Befund.'}
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {befunde.map((b) => (
              <div
                key={b.id}
                className={b.schwere === 'fehler' ? 'hinweis warnung' : 'hinweis'}
                style={{ display: 'block' }}
              >
                <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                  <span className={`marke ${b.schwere === 'fehler' ? 'warnung' : 'neutral'}`}>
                    {b.schwere}
                  </span>
                  <strong>{b.titel}</strong>
                  {b.raumnummer && (
                    <span className="schwach" style={{ fontSize: 12 }}>
                      Raum {b.raumnummer}
                    </span>
                  )}
                  <code className="schwach" style={{ fontSize: 11, marginLeft: 'auto' }}>
                    {b.schluessel}
                  </code>
                </div>

                {b.text && (
                  <p style={{ margin: '6px 0 0', fontSize: 13 }}>{b.text}</p>
                )}

                {b.vorschlag && (
                  <p className="schwach" style={{ margin: '6px 0 0', fontSize: 13 }}>
                    <strong>Was zu tun wäre:</strong> {b.vorschlag}
                  </p>
                )}

                {b.status === 'akzeptiert' ? (
                  <p className="schwach" style={{ margin: '6px 0 0', fontSize: 12 }}>
                    Akzeptiert von {b.akzeptiertVon}: {b.akzeptiertBegruendung}
                  </p>
                ) : (
                  <button
                    style={{ marginTop: 8, fontSize: 12 }}
                    onClick={() => akzeptieren(b)}
                  >
                    Mit Begründung akzeptieren
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* --- Was der Lauf tut ---------------------------------------------- */}
      <div className="karte" style={{ marginTop: 16 }}>
        <h2 className="karte-titel" style={{ marginTop: 0 }}>Was der Lauf tut</h2>

        <p>
          Er liest <strong>durch die Verknüpfungen hindurch</strong>, eine Ebene
          tief. Das ist der Unterschied zu allen anderen Aufnahmen: Die Wände
          liegen im Architekturmodell, das bei uns nur als Verknüpfung erscheint.
        </p>

        <ul style={{ lineHeight: 1.8 }}>
          <li>
            <strong>Bauteile</strong> mit Fläche, Orientierung, Neigung und
            Schichtaufbau — und <strong>allen</strong> Parametern je Bauteil und
            Typ. Welcher davon den U-Wert trägt, entscheidet die Mapping-Tabelle
            in der Datenbank, nicht der Code.
          </li>
          <li>
            <strong>Raumbegrenzungen</strong> samt Endpunkten, und die Umrisse
            flächiger Bauteile. Daraus entsteht die Boden- und Deckenzuordnung
            über echte Flächenüberschneidung — ein Umschließungsrechteck ordnet
            bei L-förmigen Räumen falsch zu.
          </li>
          <li>
            <strong>Den Modellstand</strong> jeder Verknüpfung. Ein Ergebnis aus
            fremden Modellen ist nur reproduzierbar, wenn feststeht, welche das
            waren.
          </li>
        </ul>

        <p className="schwach" style={{ fontSize: 13 }}>
          Fehlt ein U- oder g-Wert und lässt er sich auch nicht aus dem
          Schichtaufbau herleiten oder über den Nachweis zuordnen, entsteht ein
          Prüfbefund — <strong>kein Ersatzwert</strong>.
        </p>
      </div>

      {!quelle && (
        <div className="karte" style={{ marginTop: 16 }}>
          <div className="hinweis warnung">
            <span className="hinweis-zeichen"><IconWarnung size={15} /></span>
            <div>
              <strong>Noch kein Quellmodell markiert.</strong> Unter{' '}
              <em>Einstellungen → Modellzuordnung</em> das Modell ankreuzen, das
              die Architektur-Verknüpfung trägt.
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Der Rahmen um die fünf Heiz-/Kühllast-Seiten — Reiter als echte Routen.
 *
 * ═══ Warum eine Layoutroute (Muster N1, Plan Abschnitt 2 und 7) ══════════
 *
 * Bis N1 stand hier EINE Route mit fünf internen Reitern
 * (`useState<'waerme' | 'kuehl' | 'jahr' | 'klima' | 'allgemein'>`) —
 * Zustand in der Komponente statt eines Menüwegs, derselbe Zustand, den
 * Sanitär bis zum 20.09.2026 hatte. Jetzt ist jeder Reiter eine eigene
 * Unterroute unter `heizlast-kuehllast`; `LocalTabs` (Auftrag §1) ersetzt
 * die Knopfzeile 1:1, mit denselben Klassen (`unterreiter`, `aktiv`). Der
 * Pfad `heizlast-kuehllast` selbst ÄNDERT SICH NICHT — er zeigt weiterhin
 * den Index-Reiter (`HeizlastRechnen`, vormals "Wärme").
 *
 * Zwei der fünf Reiter blieben unangetastet und wurden NICHT eigens
 * gesplittet, sondern sind über die Wrapper `KuehllastSeite` und
 * `KuehllastJahresgangSeite` (`Kuehllast.tsx`) eigene Routen geworden: Die
 * beiden Komponenten `KuehllastReiter`/`JahresgangReiter` fetchen sich
 * ihre Daten schon immer selbst und brauchten keine Trennung.
 *
 * Die drei verbliebenen ("Wärme", "Klima", "Allgemein") teilten sich BISHER
 * einen einzigen Fetch-Effekt (`stand`, `extern`, `bereiche`, `referenz`,
 * `laeufe`). Jede der drei neuen Routen holt sich jetzt nur noch, was sie
 * selbst zeigt — ein zusätzlicher `heizlastStandort`-Aufruf je Route statt
 * eines gemeinsamen, aber ohne eine Rahmen-weite Datenhaltung, die bei einem
 * Berechnungslauf (`rechnen()`, nur in "Heizlast" gebraucht) mit den
 * rein lesenden Karten in "Klima"/"Allgemein" hätte abgestimmt werden
 * müssen. Das Risiko einer solchen Kopplung stand in keinem Verhältnis zum
 * Nutzen für dieses Paket.
 *
 * Der Kontext wird unverändert an den `Outlet` weitergereicht — derselbe
 * Grund wie bei `ElektroRahmen`: `useOutletContext` reicht nur eine Ebene
 * weit, ohne die Weitergabe bekäme jede Kindseite `undefined`.
 */
export function HeizlastKuehllastRahmen() {
  const kontext = useOutletContext<Rahmen>();

  return (
    <>
      <LocalTabs
        ariaLabel="Heiz-/Kühllast"
        eintraege={[
          { to: '.', beschriftung: 'Heizlast', end: true },
          { to: 'kuehllast', beschriftung: 'Kühllast' },
          { to: 'jahresgang', beschriftung: 'Jahresgang' },
          { to: 'klima', beschriftung: 'Standort und Klima' },
          { to: 'raumtemperaturen', beschriftung: 'Raumtemperaturen' },
          { to: 'allgemein', beschriftung: 'Allgemein' },
          { to: 'annahmen', beschriftung: 'Annahmen und Vorgaben' },
          { to: 'geg-abgleich', beschriftung: 'GEG-Abgleich' },
        ]}
      />
      <Raumstandhinweis api={kontext.api} projektId={kontext.projekt.id}
        rechnungen={['heizlast', 'kuehllast', 'raumtemperaturen']} />
      {/* TL2 (TX-STANDARD S1.2): "n unbestaetigte Annahmen" mit Verweis. */}
      <Annahmenhinweis api={kontext.api} projektId={kontext.projekt.id} />
      <Outlet context={kontext} />
    </>
  );
}

/**
 * Heiz-/Kühllast → Heizlast (Reiter "Heizlast", Index-Route unter
 * `heizlast-kuehllast` — derselbe Pfad wie vor N1, keine Änderung für
 * bestehende Lesezeichen).
 *
 * Der Berechnungslauf: Außentemperatur wählen, rechnen, Ergebnis samt
 * Jahressimulation und Raumtabelle. Eigener, schlankerer Datenabruf
 * (`heizlastStandort` nur für die Norm-Außentemperatur, `heizlastLaeufe` /
 * `heizlastLauf` für den Lauf) — unabhängig von "Standort und Klima".
 */
export function HeizlastRechnen() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [stand, setStand] = useState<HeizlastStandortstand | null>(null);
  const [laeufe, setLaeufe] = useState<HeizlastLaufKopf[]>([]);
  const [letzterLauf, setLetzterLauf] = useState<HeizlastLaufErgebnis | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [laufFehler, setLaufFehler] = useState<string | null>(null);
  const [detail, setDetail] = useState<HeizlastLaufDetail | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  // TL1: Welcher Lauf gilt, sagt der Server (Auslegungslauf.cs). Angezeigt
  // wird der maßgebende Lauf; ein anderer nur auf ausdrücklichen Wunsch und
  // dann mit dem Hinweis, dass er nicht maßgebend ist.
  const [wahl, setWahl] = useState<AuslegungslaufWahl | null>(null);
  const [gezeigtId, setGezeigtId] = useState<string | null>(null);
  const [bezeichnung, setBezeichnung] = useState('');

  // Als Text und nicht als number gehalten: Ein number-Feld macht aus einem
  // halb getippten "-1" schon eine Eingabe, und beim Löschen des Feldes
  // liefert es NaN statt einer leeren Zeichenkette.
  //
  // null heißt "noch nicht angefasst" und zeigt die Norm-Außentemperatur des
  // Leitdatensatzes. Über einen Effekt gesetzt wäre der Wert einen Wimpernschlag
  // lang leer und würde eine Eingabe des Benutzers wieder überschreiben, wenn
  // der Standort nachlädt.
  const [aussenText, setAussenText] = useState<string | null>(null);

  useEffect(() => {
    let abgebrochen = false;

    (async () => {
      try {
        const [s, l, a] = await Promise.all([
          api.heizlastStandort(projekt.id),
          api.heizlastLaeufe(projekt.id),
          api.heizlastAuslegungslauf(projekt.id),
        ]);
        if (abgebrochen) return;
        setStand(s); setLaeufe(l); setWahl(a.wahl);

        // Das Detail des MASSGEBENDEN Laufs gleich mit (TL1, nicht mehr des
        // jüngsten): Der Vorbehalt soll beim Öffnen der Seite sichtbar sein.
        const id = a.wahl?.laufId ?? null;
        setGezeigtId(id);
        if (id) {
          const d = await api.heizlastLauf(id);
          if (!abgebrochen) setDetail(d);
        }
        setFehler(null);
      } catch (err) {
        if (!abgebrochen) setFehler(err instanceof Error ? err.message : String(err));
      }
    })();

    return () => { abgebrochen = true; };
  }, [api, projekt.id]);

  // Der Lauf laeuft synchron durch: Bei 1.021 Raeumen dauert er Sekunden,
  // und ein Auftrag mit Fortschrittsanzeige waere hier mehr Gehaeuse als
  // Inhalt. Wird er langsamer, gehoert er in die Auftragsstruktur.
  const rechnen = async () => {
    setLaeuft(true);
    setLaufFehler(null);
    try {
      const e = await api.heizlastLaufRechnen(
        projekt.id, bezeichnung.trim() || undefined, aussen ?? undefined);
      setLetzterLauf(e);
      const [l, a] = await Promise.all([
        api.heizlastLaeufe(projekt.id), api.heizlastAuslegungslauf(projekt.id)]);
      setLaeufe(l); setWahl(a.wahl);
      // Der neue Lauf wird gezeigt - ist er nicht maßgebend, sagt es der Hinweis.
      setGezeigtId(e.laufId);
      setDetail(await api.heizlastLauf(e.laufId));
    } catch (err) {
      setLaufFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setLaeuft(false);
    }
  };

  // TL1: einen anderen Lauf ansehen - nur ansehen, nicht auswählen.
  const zeigen = async (id: string) => {
    setGezeigtId(id);
    try {
      setDetail(await api.heizlastLauf(id));
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
    }
  };

  // TL1: Kennzeichnung "maßgebend" - danach Liste und Wahl neu laden.
  const kennzeichnen = async (laufId: string, begruendung: string, zurueck: boolean) => {
    const antwort = await api.heizlastMassgebend(laufId, begruendung, zurueck);
    const [l, a] = await Promise.all([
      api.heizlastLaeufe(projekt.id), api.heizlastAuslegungslauf(projekt.id)]);
    setLaeufe(l); setWahl(a.wahl);
    return antwort.message;
  };

  const gezeigt = laeufe.find((l) => l.id === gezeigtId) ?? null;

  // Die Norm-Außentemperatur des Leitdatensatzes: Voreinstellung des Feldes
  // und Bezug des Hinweises. Fest verdrahtet wäre sie an dem Tag falsch, an
  // dem ein anderer Standort gerechnet wird.
  const normAussen = (stand?.klima ?? []).find(
    (k) => k.feldschluessel === 'winter_auslegungstemperatur_c',
  )?.wert?.zahlwert ?? -13.5;

  const aussenAnzeige = aussenText ?? normAussen.toLocaleString(
    'de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

  // undefined = unlesbar (Knopf sperren), null = leer (Leitdatensatz gilt).
  const aussen = ((): number | null | undefined => {
    const s = aussenAnzeige.trim().replace(',', '.');
    if (s === '') return null;
    const w = Number(s);
    // Der Bereich ist bewusst weit: Er soll Tippfehler abfangen
    // (13,5 statt -13,5) und keine fachliche Wahl einschränken.
    return Number.isFinite(w) && w >= -40 && w <= 20 ? w : undefined;
  })();

  const zahl = (n: number | null | undefined, ziffern = 0) =>
    n === null || n === undefined ? '—' : n.toLocaleString('de-DE', {
      minimumFractionDigits: ziffern, maximumFractionDigits: ziffern });

  return (
    <>
      {fehler && <div className="karte fehler">{fehler}</div>}

      {/* --- Der Berechnungslauf --------------------------------------------- */}
      <div className="karte" style={{ marginTop: 16 }}>
        <div className="bereich-kopf">
          <h2 className="karte-titel" style={{ margin: 0 }}>Heizlast rechnen</h2>
          {laeufe.length > 0 && (
            <span className="marke neutral">{laeufe.length} Läufe</span>
          )}
        </div>

          <Erlaeuterung>Transmission + Wärmebrücken + Infiltration beziehungsweise
          hygienischer Mindestluftwechsel + Lüftungsanteil, raumweise gegen
          die Norm-Außentemperatur. Der Lüftungsanteil ist die
          Erhitzerleistung der Anlagen (θ nach WRG → θ_Zuluft) und die
          Raumlast der Lüftung (θ_Zuluft → θ_Raum); beide stecken IN der
          Gebäudeheizlast und werden daneben einzeln ausgewiesen
          (Festlegung des Fachplaners vom 16.09.2026, Frage 14, am selben Tag
          berichtigt). Ein Lauf ist unveränderlich und ersetzt keinen
          früheren.</Erlaeuterung>

        <div style={{ display: 'flex', alignItems: 'flex-end',
                      gap: 12, flexWrap: 'wrap' }}>
          <label style={{ display: 'block' }}>
            <span className="schwach" style={{ fontSize: 12,
                                               display: 'block' }}>
              Außentemperatur (°C)
            </span>
            <input className="feld" style={{ width: 120 }}
                   value={aussenAnzeige} inputMode="decimal"
                   onChange={(ev) => setAussenText(ev.target.value)} />
          </label>

          <Feld beschriftung="Bezeichnung des Laufs" wert={bezeichnung} breite="drittel"
                platzhalter="z. B. Vergleich MBBM"
                beiAenderung={(w) => setBezeichnung((w as string) ?? '')} />

          <button className="knopf" disabled={laeuft || aussen === undefined}
                  onClick={() => void rechnen()}>
            {laeuft ? 'rechnet …' : 'Lauf rechnen'}
          </button>

          {aussen === undefined ? (
            <span className="fehler" style={{ fontSize: 12 }}>
              Keine Zahl. Zulässig ist -40 bis +20 °C.
            </span>
          ) : aussen !== null && Math.abs(aussen - normAussen) > 0.05 && (
            // Ein abweichender Wert ist kein Fehler, aber er darf nicht
            // unbemerkt bleiben: Das Ergebnis ist dann keine Normheizlast.
            <span className="warnung-text" style={{ fontSize: 12 }}>
              Abweichend von der Norm-Außentemperatur {zahl(normAussen, 1)} °C
              — das Ergebnis ist keine Normheizlast nach DIN/TS 12831-1. Der Lauf
              wird als Vergleichslauf gespeichert; Übergaben und Berichte nutzen
              ihn nicht.
            </span>
          )}
        </div>

        {laufFehler && (
          <p className="fehler" style={{ marginTop: 8 }}>{laufFehler}</p>
        )}

        {/* Der Vorbehalt steht als eigener Block und nicht als Tooltip.
            Die Vorgabe des Fachplaners war ausdrücklich, in den Ergebnissen
            darauf hinzuweisen, dass die Geometrie nicht lückenlos abgebildet
            werden kann. Ein Hinweis, den man erst beim Überfahren sieht,
            erfüllt das nicht. */}
        {laeufe.length > 0 && (
          <Auslegungshinweis art="Heizlast" wahl={wahl} gezeigt={gezeigt} />
        )}

        {gezeigt?.vorbehalte && (
          <div className="karte warnung" style={{ marginTop: 12, padding: 12 }}>
            <strong>Vorbehalte dieses Laufs</strong>
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {gezeigt.vorbehalte.split('; ').map((v, i) => (
                <li key={i}>{v}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Zonen mit Ersatzkörper: eigener Block, weil ihr Ergebnis auf
            Annahmen beruht und nicht raumweise hergeleitet ist. */}
        {detail?.raeume.filter((r) => r.istZone).map((z) => (
          <div key={z.id} className="karte warnung"
               style={{ marginTop: 12, padding: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between',
                          flexWrap: 'wrap', gap: 8 }}>
              <strong>{z.raumname ?? z.zone}</strong>
              <span>{zahl(z.normheizlastW / 1000, 1)} kW</span>
            </div>
            <div className="schwach" style={{ fontSize: 12, marginTop: 4 }}>
              Transmission {zahl((z.transmissionW + z.waermebrueckeW) / 1000, 1)} kW ·
              Mindestluft {zahl((z.mindestluftW ?? 0) / 1000, 1)} kW ·
              Infiltration {zahl(z.infiltrationW / 1000, 1)} kW ·
              Erhitzer {zahl(z.lueftungW / 1000, 1)} kW ·
              Raumlüftung {zahl((z.raumlueftungW ?? 0) / 1000, 1)} kW ·
              {z.aussenluftM3h !== null
                && ` Außenluft ${zahl(z.aussenluftM3h)} m³/h`}
              {z.wrgGradNenn !== null && z.wrgGradWirksam !== null
                && ` · WRG ${zahl(z.wrgGradNenn * 100, 1)} % nominal,
                     ${zahl(z.wrgGradWirksam * 100, 1)} % wirksam`}
            </div>
            {z.vorbehalt && (
              <p style={{ marginTop: 8, marginBottom: 0, fontSize: 12 }}>
                <strong>Vorbehalt:</strong> {z.vorbehalt}
              </p>
            )}
            {z.warnungen && (
              <p className="schwach" style={{ marginTop: 4, marginBottom: 0,
                                              fontSize: 11 }}>
                {z.warnungen}
              </p>
            )}
          </div>
        ))}

        {letzterLauf && (
          <div style={{ marginTop: 12 }}>
            <p>
              <strong>Lauf {letzterLauf.laufnummer}</strong>:{' '}
              {zahl(letzterLauf.normheizlastW / 1000, 0)} kW über{' '}
              {zahl(letzterLauf.raeume)} Räume, Vollständigkeit{' '}
              {zahl(letzterLauf.vollstaendigkeitProzent, 1)} %{' '}
              {letzterLauf.istVorlaeufig
                ? <span className="marke warnung">vorläufig</span>
                : <span className="marke ok">freigabefähig</span>}
            </p>
            <ul className="bereich-stand">
              {letzterLauf.zusammenfassung.map((z, i) => (
                <li key={i} className={z.startsWith('ACHTUNG') ? 'fehler' : undefined}>
                  {z}
                </li>
              ))}
            </ul>
          </div>
        )}

        {gezeigt && (
          <Berichtsknoepfe
            ebenen={[...new Set((detail?.raeume ?? [])
              .map((r) => r.ebene).filter((e): e is string => !!e))].sort()}
            laden={(umfang, ebene) =>
              api.heizlastBericht(gezeigt.id, umfang, ebene)}
            excel={() => api.heizlastListe(projekt.id, gezeigt.id)} />
        )}

        {gezeigt && (
          <Kennzeichnen laufnummer={gezeigt.laufnummer}
                        istMassgebend={gezeigt.istMassgebend ?? false}
                        beiSenden={(b, z) => kennzeichnen(gezeigt.id, b, z)} />
        )}

        {laeufe.length > 0 && (
          <div className="tabellen-rahmen" style={{ marginTop: 12 }}>
            <table className="liste">
              <thead>
                <tr>
                  <th>Lauf</th>
                  <th style={{ textAlign: 'right' }}>Norm-Heizlast</th>
                  <th style={{ textAlign: 'right' }}>Transmission</th>
                  <th style={{ textAlign: 'right' }} title="Hygienischer Mindestluftwechsel nach EN 12831-1 und Infiltration - Teil der Gebäudeheizlast (Frage 12)">Mindestluft</th>
                  <th style={{ textAlign: 'right' }} title="Erhitzerleistung der Lüftungsanlagen (θ nach WRG → θ_Zuluft) - Teil der Gebäudeheizlast (Festlegung 16.09.2026, Frage 14, berichtigt)">Erhitzer</th>
                  <th style={{ textAlign: 'right' }} title="Raumlast der Lüftung, wo die Zuluft kälter ist als der Raum (θ_Zuluft → θ_Raum) - Teil der Gebäudeheizlast">Raumlüftung</th>
                  <th style={{ textAlign: 'right' }}>Vollst.</th>
                  <th>Stand</th>
                  <th>Kennung</th>
                  <th><span className="m-nur-vorlesen">Aktion</span></th>
                </tr>
              </thead>
              <tbody>
                {laeufe.map((l) => (
                  <tr key={l.id}>
                    <td>
                      <strong>{l.laufnummer}</strong>
                      {l.aussentemperaturC !== null && (
                        <span className="schwach"> bei {zahl(l.aussentemperaturC, 1)} °C</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {zahl((l.normheizlastW ?? 0) / 1000, 0)} kW
                    </td>
                    <td style={{ textAlign: 'right' }} className="schwach">
                      {zahl(((l.transmissionW ?? 0) + (l.waermebrueckeW ?? 0)) / 1000, 0)} kW
                    </td>
                    <td style={{ textAlign: 'right' }} className="schwach">
                      {zahl(((l.infiltrationW ?? 0) + (l.mindestluftW ?? 0)) / 1000, 0)} kW
                    </td>
                    <td style={{ textAlign: 'right' }} className="schwach">
                      {zahl((l.lueftungW ?? 0) / 1000, 0)} kW
                    </td>
                    <td style={{ textAlign: 'right' }} className="schwach">
                      {zahl((l.raumlueftungW ?? 0) / 1000, 0)} kW
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {zahl(l.vollstaendigkeitProzent, 1)} %
                    </td>
                    <td title={l.vorbehalte ?? undefined}>
                      {l.istVorlaeufig
                        ? <span className="marke warnung">vorläufig</span>
                        : <span className="marke ok">freigabefähig</span>}
                    </td>
                    <td><Laufmarke lauf={l} wahl={wahl} /></td>
                    <td>
                      {l.id === gezeigtId
                        ? <span className="schwach">angezeigt</span>
                        : <button type="button" className="m-knopf"
                                  aria-label={`Lauf ${l.laufnummer} anzeigen`}
                                  onClick={() => void zeigen(l.id)}>anzeigen</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* --- Der Vergleich nach Norm mit üblichen Werten -------------------
          Migration 0488, Befunde H-02, H-03 und H-16. Er steht zwischen dem
          Lauf und den Räumen: Wer die Gebäudezahl gesehen hat, fragt als
          Nächstes, welche Räume sie tragen - und die stehen darunter. */}
      <Normvergleichskarte lauf={detail?.lauf ?? gezeigt} />

      {/* --- Jahressimulation ------------------------------------------------ */}
      <Heizjahresgang api={api} lauf={gezeigt} />

      {/* --- Die Räume des Laufs --------------------------------------------- */}
      {detail && detail.raeume.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <Raumergebnisse api={api} detail={detail} />
        </div>
      )}
    </>
  );
}

/**
 * Heiz-/Kühllast → Standort und Klima (Reiter "Standort und Klima").
 *
 * Die Trennung der drei Karten ist keine Gestaltungsfrage, sondern die
 * fachliche Aussage dieses Bereichs:
 *
 *   Klimadaten          gehen in die Rechnung ein
 *   Randbedingungen     gehen in die Rechnung ein
 *   Referenzwerte       gehen NICHT ein — sie prüfen das Ergebnis
 *
 * Stünden die Referenzwerte zwischen den Eingangsgrößen, läge der Gedanke
 * nahe, den fremden Gesamtbedarf zu übernehmen. Damit wäre die eigene
 * Rechnung eine Abschrift, und ein Fehler im Modell fiele nie auf.
 */
export function HeizlastKlimaSeite() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [stand, setStand] = useState<HeizlastStandortstand | null>(null);
  const [extern, setExtern] = useState<HeizlastExternwerte | null>(null);
  const [bereiche, setBereiche] = useState<HeizlastBereich[]>([]);
  const [referenz, setReferenz] = useState<HeizlastReferenzwerte | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    let abgebrochen = false;

    (async () => {
      try {
        const [s, e, b, r] = await Promise.all([
          api.heizlastStandort(projekt.id),
          api.heizlastExternwerte(projekt.id),
          api.heizlastBereiche(projekt.id),
          api.heizlastReferenzwerte(projekt.id),
        ]);
        if (abgebrochen) return;
        setStand(s); setExtern(e); setBereiche(b); setReferenz(r);
        setFehler(null);
      } catch (err) {
        if (!abgebrochen) setFehler(err instanceof Error ? err.message : String(err));
      }
    })();

    return () => { abgebrochen = true; };
  }, [api, projekt.id]);

  const pflicht = (stand?.klima ?? []).filter((k) => k.istPflicht);
  const belegt = pflicht.filter((k) => k.wert && (
    k.wert.zahlwert !== null || k.wert.textwert !== null));
  const annahmen = belegt.filter((k) => k.wert?.istAnnahme);

  const zahl = (n: number | null | undefined, ziffern = 0) =>
    n === null || n === undefined ? '—' : n.toLocaleString('de-DE', {
      minimumFractionDigits: ziffern, maximumFractionDigits: ziffern });

  return (
    <>
          {fehler && <div className="karte fehler">{fehler}</div>}
      {/* --- Standort und Klima -------------------------------------------- */}
      <div className="karte">
        <div className="bereich-kopf">
          <h2 className="karte-titel" style={{ margin: 0 }}>Standort und Klima</h2>
          {stand?.datensatz?.istVorlaeufig
            ? <span className="marke warnung">vorläufig</span>
            : stand?.datensatz
              ? <span className="marke ok">belegt</span>
              : <span className="marke neutral">kein Datensatz</span>}
        </div>

        {stand?.standort && (
          <p className="schwach" style={{ marginTop: 8 }}>
            {zahl(stand.standort.breiteGrad, 4)}° N /{' '}
            {zahl(stand.standort.laengeGrad, 4)}° O,{' '}
            {zahl(stand.standort.hoeheMuNn, 2)} m ü. NN — Herkunft{' '}
            <strong>{stand.standort.herkunft}</strong>, Güte{' '}
            {stand.standort.guete}
            {stand.standort.istGepflegt && ' (von Hand gepflegt, wird nicht überschrieben)'}
          </p>
        )}

        {stand?.datensatz && (
          <p style={{ marginTop: 4 }}>
            <strong>{stand.datensatz.bezeichnung}</strong>
            {stand.datensatz.station && ` — Station ${stand.datensatz.station}`}
            {stand.datensatz.abstandKm !== null
              && `, ${zahl(stand.datensatz.abstandKm, 1)} km entfernt`}
          </p>
        )}

        <p style={{ marginTop: 8 }}>
          <strong>{belegt.length} von {pflicht.length}</strong> Pflichtangaben belegt
          {annahmen.length > 0 && (
            <>, davon <strong>{annahmen.length} als Annahme</strong> gekennzeichnet</>
          )}.
        </p>

        {annahmen.length > 0 && (
          <Erlaeuterung>Eine Annahme verhindert die Freigabe. Sie verschwindet nicht durch
            einen erneuten Lauf, sondern durch eine Angabe des Fachplaners.</Erlaeuterung>
        )}

        {pflicht.length > 0 && (
          <div className="tabellen-rahmen">
            <table className="liste">
              <thead>
                <tr>
                  <th>Pflichtangabe</th>
                  <th style={{ textAlign: 'right' }}>Wert</th>
                  <th>Herkunft</th>
                  <th>Quelle</th>
                </tr>
              </thead>
              <tbody>
                {pflicht.map((k) => (
                  <tr key={k.feldschluessel}>
                    <td>{k.bezeichnung}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {k.wert?.zahlwert !== null && k.wert?.zahlwert !== undefined
                        ? `${k.wert.zahlwert.toLocaleString('de-DE')} ${k.einheit ?? ''}`
                        : k.wert?.textwert ?? <span className="schwach">fehlt</span>}
                    </td>
                    <td>
                      {k.wert?.herkunft ?? '—'}
                      {k.wert?.istAnnahme && (
                        <> <span className="marke warnung">Annahme</span></>
                      )}
                    </td>
                    <td className="schwach" style={{ maxWidth: 420 }}>
                      {k.wert?.quelle ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {(stand?.weitereDatensaetze.length ?? 0) > 0 && (
          <>
            <h3 style={{ marginTop: 20, marginBottom: 4 }}>
              Weitere Datensätze
            </h3>
            <p className="schwach" style={{ marginTop: 0 }}>
              Auslegungstage sind Lastfälle, die gerechnet werden — sie tragen
              die Pflichtangaben nicht.
            </p>
            <div className="tabellen-rahmen">
              <table className="liste">
                <thead>
                  <tr>
                    <th>Verwendung</th>
                    <th>Datensatz</th>
                    <th>Station</th>
                    <th style={{ textAlign: 'right' }}>Zeitschritte</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {stand!.weitereDatensaetze.map((d) => (
                    <tr key={d.id}>
                      <td><span className="marke neutral">{d.verwendung}</span></td>
                      <td title={d.hinweis ?? undefined}>{d.bezeichnung}</td>
                      <td className="schwach">
                        {d.stationskennung ?? '—'}
                        {d.abstandKm !== null && ` · ${zahl(d.abstandKm, 1)} km`}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {d.zeitschritte > 0 ? zahl(d.zeitschritte) : '—'}
                      </td>
                      <td className="schwach">{d.datenstatus ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {stand!.weitereDatensaetze.some((d) => d.zitierhinweis) && (
              <p className="schwach" style={{ marginTop: 8 }}>
                {stand!.weitereDatensaetze.find((d) => d.zitierhinweis)!.zitierhinweis}
              </p>
            )}
          </>
        )}
      </div>

      {/* --- Übernommene Randbedingungen ------------------------------------ */}
      {extern && extern.anzahl > 0 && (
        <div className="karte" style={{ marginTop: 16 }}>
          <div className="bereich-kopf">
            <h2 className="karte-titel" style={{ margin: 0 }}>
              Übernommene Randbedingungen
            </h2>
            <span className="marke neutral">{extern.anzahl} Angaben</span>
          </div>

          <Erlaeuterung>Mengenangaben einer externen Fachsimulation. Sie stehen NEBEN dem
            Raumbuch, nicht darin — ein Raumbuchwert wird davon nicht
            verdrängt.</Erlaeuterung>

          <div className="tabellen-rahmen">
            <table className="liste">
              <thead>
                <tr>
                  <th>Kategorie</th>
                  <th>Bereich</th>
                  <th>Angabe</th>
                  <th style={{ textAlign: 'right' }}>Wert</th>
                  <th>Zuordnung</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {extern.werte.map((w) => (
                  <tr key={w.id} style={w.istHistorisch ? { opacity: 0.55 } : undefined}>
                    <td className="schwach">{w.kategorie}</td>
                    <td>{w.bereich ?? '—'}</td>
                    <td title={w.hinweis ?? w.bemerkung ?? undefined}>
                      {w.bezeichnung}
                      {w.istHistorisch && (
                        <> <span className="marke neutral">historisch</span></>
                      )}
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {w.zahlwert !== null
                        ? `${w.zahlwert.toLocaleString('de-DE')} ${w.einheit ?? ''}`
                        : <span className="schwach">keine Angabe</span>}
                    </td>
                    <td>
                      {w.zuordnungsart === 'raum_eindeutig'
                        ? <>{w.raumnummer}</>
                        : w.zuordnungsart === 'nicht_zugeordnet'
                          ? <span className="marke warnung">kein Raum</span>
                          : <span className="schwach">
                              {w.zuordnungsart === 'bereich_mehrere_raeume'
                                ? `${w.raeumeAnzahl ?? 0} Räume` : 'Gebäude'}
                            </span>}
                    </td>
                    <td className="schwach">
                      {w.datenstatus === 'missing'
                        ? <span className="marke warnung">missing</span>
                        : w.datenstatus}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* --- Bereichsabgleich Personen -------------------------------------- */}
      {bereiche.length > 0 && (
        <div className="karte" style={{ marginTop: 16 }}>
          <h2 className="karte-titel" style={{ marginTop: 0 }}>
            Bereiche und Personenbelegung
          </h2>

          <Erlaeuterung>Der Bericht rechnet in Bereichen, das Modell in Räumen. Diese
            Zuordnung ist eine Festlegung und keine Ableitung — solange sie
            nicht bestätigt ist, trägt sie die Güte, mit der sie entstand.</Erlaeuterung>

          <div className="tabellen-rahmen">
            <table className="liste">
              <thead>
                <tr>
                  <th>Bereich</th>
                  <th style={{ textAlign: 'right' }}>Räume</th>
                  <th style={{ textAlign: 'right' }}>mit Personenzahl</th>
                  <th style={{ textAlign: 'right' }}>Personen (Raumbuch)</th>
                  <th>Güte</th>
                </tr>
              </thead>
              <tbody>
                {bereiche.map((b) => (
                  <tr key={b.bereich}>
                    <td><strong>{b.bereich}</strong></td>
                    <td style={{ textAlign: 'right' }}>{b.raeume}</td>
                    <td style={{ textAlign: 'right' }}>
                      {b.raeumeMitPersonenzahl}
                      {b.raeumeMitPersonenzahl === 0 && (
                        <> <span className="marke warnung">keine</span></>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {b.personenSumme > 0 ? zahl(b.personenSumme) : '—'}
                    </td>
                    <td>
                      {b.guete.map((g) => (
                        <span key={g}
                              className={`marke ${g === 'eindeutig' ? 'ok' : 'neutral'}`}>
                          {g}
                        </span>
                      ))}
                      {b.bestaetigt === 0 && (
                        <span className="schwach"> unbestätigt</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* --- Externe Referenzwerte ------------------------------------------ */}
      {referenz && referenz.werte.length > 0 && (
        <div className="karte" style={{ marginTop: 16 }}>
          <div className="bereich-kopf">
            <h2 className="karte-titel" style={{ margin: 0 }}>
              Externe Referenzwerte
            </h2>
            <span className="marke warnung">kein Berechnungseingang</span>
          </div>

          <p className="schwach">
            {referenz.hinweis}
            {referenz.toleranzProzent !== null && (
              <> Zulässige Abweichung: {zahl(referenz.toleranzProzent, 0)} %.</>
            )}
          </p>

          <div className="tabellen-rahmen">
            <table className="liste">
              <thead>
                <tr>
                  <th>Fall</th>
                  <th>Szenario</th>
                  <th style={{ textAlign: 'right' }}>Leistung</th>
                  <th>Status</th>
                  <th>Verwendbar</th>
                </tr>
              </thead>
              <tbody>
                {referenz.werte.map((r) => (
                  <tr key={r.id}>
                    <td className="schwach">{r.fall}</td>
                    <td title={r.bemerkung ?? undefined}>{r.bezeichnung}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {r.leistungKw !== null
                        ? `${r.leistungKw.toLocaleString('de-DE')} kW`
                        : <span className="schwach">{r.textwert ?? 'keine Zahl'}</span>}
                    </td>
                    <td className="schwach">{r.datenstatus}</td>
                    <td>
                      {r.istAktuell
                        ? <span className="marke ok">ja</span>
                        : <span className="marke warnung" title={r.handlungsbedarf ?? undefined}>
                            nein
                          </span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {referenz.werte.some((r) => !r.istAktuell && r.handlungsbedarf) && (
            <p className="schwach" style={{ marginTop: 8 }}>
              {referenz.werte.find((r) => !r.istAktuell && r.handlungsbedarf)!.handlungsbedarf}
            </p>
          )}
        </div>
      )}
    </>
  );
}

/**
 * Heiz-/Kühllast → Allgemein (Reiter "Allgemein" — der Stand des Moduls).
 *
 * Eigene Route, weil sie nichts mit einem einzelnen Lauf zu tun hat. Sie
 * sagt, was das Modul kann und was es noch nicht kann - die Frage, die ein
 * Außenstehender zuerst stellt.
 */
export function HeizlastAllgemeinSeite() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [stand, setStand] = useState<HeizlastStandortstand | null>(null);

  const huelle = projekt.modelle.some(
    (m) => m.isActive && (m.rollen ?? []).includes('huelle'));

  useEffect(() => {
    let abgebrochen = false;

    (async () => {
      const s = await api.heizlastStandort(projekt.id);
      if (!abgebrochen) setStand(s);
    })();

    return () => { abgebrochen = true; };
  }, [api, projekt.id]);

  const pflicht = (stand?.klima ?? []).filter((k) => k.istPflicht);
  const belegt = pflicht.filter((k) => k.wert && (
    k.wert.zahlwert !== null || k.wert.textwert !== null));
  const annahmen = belegt.filter((k) => k.wert?.istAnnahme);

  return (
    <>
          <div className="karte">
            <div className="bereich-kopf">
              <h2 className="karte-titel" style={{ margin: 0 }}>
                Heiz- und Kühllast — Stand des Moduls
              </h2>
              <span className="marke laufend">in Arbeit</span>
            </div>

            <p className="schwach" style={{ marginTop: 8 }}>
              Zwei Rechnungen auf demselben Datenbestand: die Heizlast nach
              DIN EN 12831-1 und die Kühllast nach VDI 2078, dazu je eine
              Jahressimulation über die DWD-Messreihe für München-Flughafen.
            </p>

            <h3 className="karte-titel" style={{ marginTop: 18 }}>
              Was steht
            </h3>
            <ul className="bereich-stand">
              <li>
                <strong>Datenmodell und Hüllenaufnahme</strong> — Bauteile,
                Schichten, Raumbegrenzungen, Parameterkatalog, GEG-Nachweis.
                Die Aufnahme lässt sich ohne Revit erneut einspielen.
              </li>
              <li>
                <strong>Raum-Bauteilzuordnung und Nachweisverknüpfung</strong>{' '}
                — zu bedienen unter <em>Gebäudehülle</em>. Von Hand gesetzte
                Randbedingungen überleben einen Neulauf.{' '}
                {huelle
                  ? 'Ein Quellmodell für die Hülle ist markiert.'
                  : 'Es ist noch kein Quellmodell für die Hülle markiert.'}
              </li>
              <li>
                <strong>Standort und Klima</strong> —{' '}
                {pflicht.length > 0 && belegt.length === pflicht.length
                  ? <>alle {pflicht.length} Pflichtangaben belegt
                      {annahmen.length > 0
                        && `, ${annahmen.length} davon als Annahme`}.</>
                  : 'offen.'}{' '}
                8.760 Stunden Messreihe, Lücken aus der Nachbarstation
                geschlossen und als solche gekennzeichnet.
              </li>
              <li>
                <strong>Heizlast</strong> — Transmission, Wärmebrücken,
                Lüftung mit temperaturabhängigem WRG-Frostschutz,
                Infiltration, Aufheizleistung. Außentemperatur je Lauf
                wählbar. Ausgabe mit H<sub>T</sub>, H<sub>V</sub>, W/m² und
                W/m³ sowie Bauteilnachweis mit Kürzel und Himmelsrichtung.
              </li>
              <li>
                <strong>Kühllast</strong> — Auslegungstag stundenweise:
                Sonnenlufttemperatur, solarer Eintrag über g-Wert, innere
                Lasten nach Stundenprofil, Lüftung mit Rückgewinnung.
                Trockene und feuchte Last getrennt.
              </li>
              <li>
                <strong>Jahressimulationen</strong> — Heizwärmebedarf und
                Kühlarbeit über alle 8.760 Stunden, mit Vollbenutzungsstunden
                und freier Kühlung.
              </li>
              <li>
                <strong>Die Bowl als Zone</strong> — ein Luftraum über alle
                Ränge mit Ersatzkörper und Veranstaltungsprofil, statt 76
                einzelner Rangblöcke.
              </li>
              <li>
                <strong>Berichte</strong> — PDF je Modul in drei Umfängen,
                wahlweise geschossweise.
              </li>
            </ul>

            <h3 className="karte-titel" style={{ marginTop: 18 }}>
              Was noch fehlt
            </h3>
            <ul className="bereich-stand">
              <li>
                <strong>Erdreich nach EN ISO 13370</strong> — gerechnet wird
                ersatzweise pauschal mit b = 0,30. Alle 82
                Erdreichzuordnungen haben keine Verknüpfung zu einem
                GEG-Bauteiltyp.
              </li>
              <li>
                <strong>Speicherwirkung in der Kühllast</strong> — jeder
                Eintrag wird in der Stunde angesetzt, in der er auftritt. Die
                Spitzen fallen dadurch zu hoch und zu früh aus. Für einen
                Nachweis nach VDI 2078 reicht das nicht.
              </li>
              <li>
                <strong>Sonnenschutz</strong> — im Nachweis ist an keinem
                transparenten Bauteiltyp einer hinterlegt, gerechnet wird
                mit F<sub>c</sub> = 1,0.
              </li>
              <li>
                <strong>Aliaszuordnung der Bauteiltypen</strong> — die
                Arbeitsliste unter <em>docs/</em> führt die Typen ohne
                Nachweiscode.
              </li>
              <li>
                <strong>Laufvergleich Seite an Seite</strong> und die
                Übergabe der Ergebnisse an die Anlagenauslegung.
              </li>
            </ul>

            <p className="sehr-schwach" style={{ marginTop: 14 }}>
              Die offenen Befunde stehen unter <em>Gebäudehülle → Prüfungen</em>.
              Sie sind der verbindliche Stand — diese Liste ist die Übersicht.
            </p>
          </div>
    </>
  );
}


/**
 * „Heizlast nach Norm mit üblichen Werten" — Befunde H-02, H-03 und H-16 der
 * Normprüfung Heizung/Kälte vom 16.09.2026.
 *
 * ═══ Warum das NEBEN dem Ergebnis steht und nicht darin ═══════════════════
 *
 * Die drei Posten verschieben die Gebäudeheizlast um zweistellige Prozent:
 * der Wiederaufheizzuschlag um 13,5 bis 30,4 %, der hygienische
 * Mindestluftwechsel in 404 von 920 Räumen, der Erdreichpfad nach
 * EN ISO 13370 um rund −4,3 %. WELCHER Wert gilt, ist eine Festlegung des
 * Fachplaners und keine Rechenfrage — keiner der Ansätze ist bestätigt.
 *
 * Deshalb bleibt Φ_HL in jeder anderen Zahl dieser Seite unberührt.
 *
 * ═══ Warum die Karte ganz verschwindet ═══════════════════════════════════
 *
 * Ein Lauf ohne Vergleichsvariante bekommt keine Überschrift mit Strichen
 * darunter. Die läse sich wie „geprüft und kein Unterschied" — und geprüft
 * wäre gar nichts.
 */
function Normvergleichskarte({ lauf }: { lauf: HeizlastLaufKopf | null }) {
  if (!lauf?.vergleichNormheizlastW) return null;

  const heute = lauf.normheizlastW ?? 0;
  const norm = lauf.vergleichNormheizlastW;
  const abweichung = heute > 0 ? ((norm - heute) / heute) * 100 : 0;

  const zahl = (n: number | null | undefined, ziffern = 1) =>
    n === null || n === undefined ? '—' : n.toLocaleString('de-DE', {
      minimumFractionDigits: ziffern, maximumFractionDigits: ziffern });

  const posten = [
    {
      bez: 'Aufheizleistung Φ_RH',
      norm: 'EN 12831-1, 6.5',
      heute: 0,
      neu: lauf.vergleichAufheizW ?? 0,
    },
    {
      bez: 'Hygienischer Mindestluftwechsel',
      norm: 'EN 12831-1, 6.3.3',
      heute: 0,
      neu: lauf.vergleichMindestluftW ?? 0,
    },
    {
      bez: 'Erdreichpfad Φ_T,ig',
      norm: 'EN ISO 13370',
      heute: lauf.erdreichHeuteW ?? 0,
      neu: lauf.vergleichErdreichW ?? 0,
    },
  ];

  return (
    <div className="karte" style={{ marginTop: 16 }}>
      <div className="bereich-kopf">
        <h2 className="karte-titel" style={{ margin: 0 }}>
          Vergleich nach Norm mit üblichen Werten
        </h2>
        <span className="marke warnung">Vergleichsrechnung, kein Ergebnis</span>
      </div>

      <Erlaeuterung>
        Was die Norm-Heizlast wäre, wenn die üblichen Werte für drei heute
        nicht gerechnete Posten angesetzt würden. Die Ansätze sind
        unbestätigt; die Festlegung trifft der Fachplaner. Φ_HL bleibt an
        jeder anderen Stelle unverändert.
      </Erlaeuterung>

      <div className="kennzahlen" style={{ marginTop: 12 }}>
        <div>
          <div className="kennzahl-wert">{zahl(heute / 1000, 1)}</div>
          <div className="kennzahl-name">kW heutiger Ansatz</div>
        </div>
        <div>
          <div className="kennzahl-wert" style={{ color: 'var(--warnung)' }}>
            {zahl(norm / 1000, 1)}
          </div>
          <div className="kennzahl-name">kW nach Norm (übliche Werte)</div>
        </div>
        <div>
          <div className="kennzahl-wert" style={{ color: 'var(--warnung)' }}>
            {abweichung >= 0 ? '+' : ''}{zahl(abweichung, 1)}
          </div>
          <div className="kennzahl-name">% Abweichung</div>
        </div>
      </div>

      <div className="tabellen-rahmen" style={{ marginTop: 12 }}>
        <table className="liste">
          <thead>
            <tr>
              <th>Posten</th>
              <th>Norm</th>
              <th style={{ textAlign: 'right' }}>heute</th>
              <th style={{ textAlign: 'right' }}>nach Norm</th>
              <th style={{ textAlign: 'right' }}>Differenz</th>
            </tr>
          </thead>
          <tbody>
            {posten.map((p) => (
              <tr key={p.bez}>
                <td>{p.bez}</td>
                <td className="sehr-schwach">{p.norm}</td>
                <td style={{ textAlign: 'right' }}>
                  {zahl(p.heute / 1000, 1)} kW
                </td>
                <td style={{ textAlign: 'right' }}>
                  {zahl(p.neu / 1000, 1)} kW
                </td>
                <td style={{ textAlign: 'right' }}>
                  {p.neu - p.heute >= 0 ? '+' : ''}
                  {zahl((p.neu - p.heute) / 1000, 1)} kW
                </td>
              </tr>
            ))}
            <tr style={{ borderTop: '1px solid var(--linie)' }}>
              <td><strong>Norm-Heizlast Φ_HL</strong></td>
              <td></td>
              <td style={{ textAlign: 'right' }}>
                <strong>{zahl(heute / 1000, 1)} kW</strong>
              </td>
              <td style={{ textAlign: 'right' }}>
                <strong>{zahl(norm / 1000, 1)} kW</strong>
              </td>
              <td style={{ textAlign: 'right', color: 'var(--warnung)' }}>
                <strong>
                  {norm - heute >= 0 ? '+' : ''}
                  {zahl((norm - heute) / 1000, 1)} kW
                </strong>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {lauf.vergleichHinweis && (
        <ul className="bereich-stand" style={{ marginTop: 10 }}>
          {lauf.vergleichHinweis.split('\n').map((h, i) => (
            <li key={i}>{h}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Eine Erlaeuterung, die zugeklappt beginnt.
 *
 * Die Seite trug elf Absaetze Fliesstext zwischen den Tabellen. Jeder einzelne
 * ist richtig und beim ersten Lesen nuetzlich - zusammen haben sie die Zahlen
 * zugeschuettet, um die es geht. Als <details> bleiben sie da, wo sie
 * hingehoeren, und kosten eine Zeile statt vier.
 */
function Erlaeuterung({ children }: { children: React.ReactNode }) {
  return (
    <details className="erlaeuterung">
      <summary>Erläuterung</summary>
      <div>{children}</div>
    </details>
  );
}

